import { z } from "zod";

/**
 * The parts of Loot OS a tenant has, switched by platform staff on `tenant_settings.modules`
 * (at creation, or later on the tenant's Settings page). A module that is off is gone from the
 * tenant's dashboard, and the server refuses its pages' reads and writes with
 * `tenant_module_disabled`. It composes as AND with the tenant's own per-environment switches
 * (`storefront_configs.enabled`, `features.*`, `intercom_enabled`): a module decides whether the
 * tenant may use a thing, the environment's switch whether it does.
 */
export const TENANT_MODULE_KEYS = [
  "packs",
  "analytics",
  "storefront",
  "battles",
  "chat",
  "forge",
  "supportChat",
  "notifications",
  "shipping",
  "shopify",
] as const;
export type TenantModuleKey = (typeof TENANT_MODULE_KEYS)[number];
export type TenantModules = Record<TenantModuleKey, boolean>;

/**
 * The storefront's own modules: off whenever `storefront` is off, whatever they say. Each but
 * `notifications` is also a per-environment switch the tenant flips (`features.battles`,
 * `features.chat`, `features.forge`, `intercom_enabled`); notifications follow the module alone,
 * each category gated by its own feature.
 */
export const STOREFRONT_SUB_MODULES = [
  "battles",
  "chat",
  "forge",
  "supportChat",
  "notifications",
] as const;
export type StorefrontSubModule = (typeof STOREFRONT_SUB_MODULES)[number];

export const TENANT_MODULE_LABELS: Record<TenantModuleKey, string> = {
  packs: "Packs",
  analytics: "Analytics",
  storefront: "Storefront",
  battles: "Battles",
  chat: "Chat",
  forge: "Forge",
  supportChat: "Support chat",
  notifications: "Notifications",
  shipping: "Shipping",
  shopify: "Shopify",
};

/**
 * Every module on: what a tenant created before modules existed has, so a stored `{}` changes
 * nothing, and a key added later needs no backfill.
 */
export const TENANT_MODULE_DEFAULTS: TenantModules = {
  packs: true,
  analytics: true,
  storefront: true,
  battles: true,
  chat: true,
  forge: true,
  supportChat: true,
  notifications: true,
  shipping: true,
  shopify: true,
};

/** The stored overrides: a partial record, `{}` = every module on. */
export const tenantModulesSchema = z.partialRecord(z.enum(TENANT_MODULE_KEYS), z.boolean());
export type TenantModuleOverrides = z.infer<typeof tenantModulesSchema>;

function isStorefrontSubModule(key: TenantModuleKey): key is StorefrontSubModule {
  return (STOREFRONT_SUB_MODULES as readonly string[]).includes(key);
}

/**
 * The stored overrides merged onto the defaults, each switch as staff last set it: what an editor
 * shows and saves back. Never a gate's answer; that is `resolveTenantModules`.
 */
export function tenantModuleSwitches(overrides?: TenantModuleOverrides | null): TenantModules {
  return { ...TENANT_MODULE_DEFAULTS, ...(overrides ?? {}) };
}

/**
 * The stored overrides merged onto the defaults, with each storefront sub-module off while the
 * storefront is: the one reading of `tenant_settings.modules` every gate and page uses.
 */
export function resolveTenantModules(overrides?: TenantModuleOverrides | null): TenantModules {
  const merged = tenantModuleSwitches(overrides);
  for (const key of TENANT_MODULE_KEYS) {
    if (isStorefrontSubModule(key) && !merged.storefront) merged[key] = false;
  }
  return merged;
}

/** A share of money as basis points, 0–10000 (100% = 10000). */
export const shareBpsSchema = z.int().min(0).max(10_000);

/**
 * The Shopify fee a new tenant starts with: 5% of settled box volume, billed through Shopify
 * (docs/shopify.md, "Install and billing"). Staff change it per tenant.
 */
export const SHOPIFY_DEFAULT_PLATFORM_SHARE_BPS = 500;

/**
 * The tenant's commercial terms as platform staff record them. The storefront share is reporting
 * only; the Shopify share is the usage fee the Shopify app bills the merchant.
 */
export const tenantCommercialTermsSchema = z.object({
  /** The platform's share of the tenant's storefront profit, in basis points. */
  storefrontProfitShareBps: shareBpsSchema,
  /**
   * The platform's fee on the tenant's settled Shopify box sales, in basis points, billed as
   * Shopify usage charges.
   */
  shopifyPlatformShareBps: shareBpsSchema,
});
export type TenantCommercialTerms = z.infer<typeof tenantCommercialTermsSchema>;

/** Basis points as a percent string for display: 1250 → "12.5%". */
export function formatShareBps(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`;
}

/**
 * Where Loot OS POSTs a tenant's order events, or null for none. HTTPS only: every request carries
 * a signature the tenant verifies, and the events name its orders.
 */
export const orderEventsUrlSchema = z
  .url({ protocol: /^https$/ })
  .max(2048)
  .nullable();
